import { useState } from 'react'
import { cleanVatRate, parsePriceCents, type ServiceDTO } from '@wf/shared'
import { VatSelect } from '../orders/VatSelect'
import { useDeleteService, usePatchService } from './useServices'

/**
 * Eine Zeile der Preisliste: Bezeichnung, Einkaufspreis, Verkaufspreis (je netto) und MwSt-Satz sind direkt
 * editierbar (wie im Alt-Monolithen). Text-/Preis-Felder schreiben beim Verlassen (onBlur), die MwSt sofort
 * bei Auswahl. Geld läuft über @wf/shared (parsePriceCents, ganze Cent). Ein leerer Name wird verworfen, ein
 * unveränderter Wert löst keinen Schreibvorgang aus.
 */
export function PreiseRow({ service }: { service: ServiceDTO }) {
  const patch = usePatchService()
  const remove = useDeleteService()
  const [name, setName] = useState(service.name)
  const [purchasePrice, setPurchasePrice] = useState((service.purchasePriceCents / 100).toFixed(2).replace('.', ','))
  const [price, setPrice] = useState((service.priceCents / 100).toFixed(2).replace('.', ','))

  function commitName() {
    const next = name.trim()
    if (!next) {
      setName(service.name)
      return
    }
    if (next !== service.name) patch.mutate({ id: service.id, body: { name: next } })
  }
  function commitPurchasePrice() {
    const cents = parsePriceCents(purchasePrice)
    if (cents !== service.purchasePriceCents) patch.mutate({ id: service.id, body: { purchasePriceCents: cents } })
  }
  function commitPrice() {
    const cents = parsePriceCents(price)
    if (cents !== service.priceCents) patch.mutate({ id: service.id, body: { priceCents: cents } })
  }
  function commitVatRate(rate: number) {
    const next = cleanVatRate(rate)
    if (next !== service.vatRate) patch.mutate({ id: service.id, body: { vatRate: next } })
  }

  return (
    <div className="wf-table__row">
      <input
        className="wf-input wf-cell wf-table__name"
        aria-label={`Bezeichnung: ${service.name}`}
        value={name}
        onChange={(event) => setName(event.target.value)}
        onBlur={commitName}
      />
      <input
        className="wf-input wf-cell wf-tnum wf-table__price"
        inputMode="decimal"
        aria-label={`Einkaufspreis: ${service.name}`}
        value={purchasePrice}
        onChange={(event) => setPurchasePrice(event.target.value)}
        onBlur={commitPurchasePrice}
      />
      <input
        className="wf-input wf-cell wf-tnum wf-table__price"
        inputMode="decimal"
        aria-label={`Verkaufspreis: ${service.name}`}
        value={price}
        onChange={(event) => setPrice(event.target.value)}
        onBlur={commitPrice}
      />
      <VatSelect value={service.vatRate} onChange={commitVatRate} label={`MwSt: ${service.name}`} />
      <button
        type="button"
        className="wf-iconbtn wf-table__del"
        aria-label={`Leistung löschen: ${service.name}`}
        disabled={remove.isPending}
        onClick={() => remove.mutate(service.id)}
      >
        ✕
      </button>
    </div>
  )
}
